/**
 * Signup funnel admin page.
 *
 * Layout:
 *   PageHeader → Cohort selector (preset windows backed by since/until)
 *              → Step-by-step funnel ribbon (each stage as a tapered block)
 *              → Drop-off reasons callout (canonical exit paths per stage)
 *              → Recent installer downloads table
 *
 * The funnel endpoint already returns aggregate stage counts +
 * stage-over-stage percentages — we just need to render them more
 * legibly than the existing horizontal bars.
 */
import { useEffect, useMemo, useState, type JSX } from 'react';
import {
  Filter, RefreshCw, Trash2, ArrowDownRight, AlertOctagon,
} from 'lucide-react';
import {
  AdminApiClient, 
  type FunnelSummary, type InstallerDownloadRow,
} from '../api';
import { userMessageFor, failureFrom, type Failure } from '../lib/api-state';
import { useSession } from '../session';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
import { StatusChip } from '../components/ui/StatusChip';
import { DataTable, type Column } from '../components/ui/DataTable';
import { FetchState } from '../components/ui/FetchState';

type CohortKey = 'this_month' | 'last_month' | 'last_3m' | '90d' | 'all';

interface CohortRange {
  key: CohortKey;
  label: string;
  hint: string;
  since: string | null;
  until: string | null;
}

function buildCohorts(): CohortRange[] {
  const now = new Date();
  const startOfThis = new Date(now.getFullYear(), now.getMonth(), 1);
  const startOfLast = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const endOfLast = new Date(now.getFullYear(), now.getMonth(), 1);
  const start3 = new Date(now.getFullYear(), now.getMonth() - 3, 1);
  const start90 = new Date(now.getTime() - 90 * 86_400_000);
  const monthLabel = (d: Date): string =>
    d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  return [
    {
      key: 'this_month',
      label: monthLabel(startOfThis),
      hint: 'current month-to-date cohort',
      since: startOfThis.toISOString(),
      until: null,
    },
    {
      key: 'last_month',
      label: monthLabel(startOfLast),
      hint: 'previous full month',
      since: startOfLast.toISOString(),
      until: endOfLast.toISOString(),
    },
    {
      key: 'last_3m',
      label: 'Last 3 months',
      hint: 'rolling 3-month cohort',
      since: start3.toISOString(),
      until: null,
    },
    {
      key: '90d',
      label: '90 days',
      hint: 'rolling 90-day window',
      since: start90.toISOString(),
      until: null,
    },
    {
      key: 'all',
      label: 'All time',
      hint: 'no since/until filter',
      since: null,
      until: null,
    },
  ];
}

interface DropOffReason {
  stage: string;
  reasons: Array<{ label: string; hint: string }>;
}

const DROP_OFF: DropOffReason[] = [
  {
    stage: 'Signup → Download',
    reasons: [
      { label: 'Email verification stalled', hint: 'Trial email landed in spam or inbox-zero queue.' },
      { label: 'Pricing reading',           hint: 'Customer compared plans and bounced before downloading.' },
    ],
  },
  {
    stage: 'Download → Activation',
    reasons: [
      { label: 'OS / arch mismatch',        hint: 'Wrong installer downloaded; abandoned without retry.' },
      { label: 'Sandbox blocking',          hint: 'macOS Gatekeeper or Windows SmartScreen quarantine.' },
      { label: 'Couldn\'t find activation key', hint: 'Customer skipped the welcome email; engine prompts.' },
    ],
  },
  {
    stage: 'Activation → Paid',
    reasons: [
      { label: 'Trial expired without conversion', hint: '14-day window ended; customer didn\'t enter billing.' },
      { label: 'Pricing pushback',          hint: 'Customer ran the trial, decided they wanted a smaller plan.' },
      { label: 'Stalled procurement',       hint: 'Enterprise; legal or security review still in flight.' },
    ],
  },
];

export function FunnelPage(): JSX.Element {
  const { state } = useSession();
  const [cohort, setCohort] = useState<CohortKey>('last_3m');
  const [data, setData] = useState<FunnelSummary | null>(null);
  const [downloads, setDownloads] = useState<InstallerDownloadRow[] | null>(null);
  const [err, setErr] = useState<Failure | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [version, setVersion] = useState(0);

  const cohorts = useMemo(buildCohorts, []);
  const active = useMemo(
    () => cohorts.find((c) => c.key === cohort) ?? cohorts[0],
    [cohorts, cohort],
  );

  const canMutate = state.user?.mfa_ok
                 && (state.user?.role === 'owner' || state.user?.role === 'admin');

  useEffect(() => {
    setRefreshing(true);
    const api = new AdminApiClient({ baseUrl: '', getToken: () => state.token });
    Promise.all([
      api.funnel({
        since: active?.since ?? undefined,
        until: active?.until ?? undefined,
      }),
      api.installerDownloads({ limit: 100 }),
    ])
      .then(([f, d]) => {
        setData(f);
        setDownloads(d.installer_downloads);
        setErr(null);
      })
      .catch((e) => setErr(failureFrom(e, 'the funnel data')))
      .finally(() => setRefreshing(false));
  }, [state.token, active, version]);

  const onRefresh = (): void => setVersion((v) => v + 1);

  const onDelete = async (id: string): Promise<void> => {
    if (!confirm('Delete this download record?')) return;
    const api = new AdminApiClient({ baseUrl: '', getToken: () => state.token });
    try {
      await api.deleteInstallerDownload(id);
      onRefresh();
    } catch (e) {
      alert(userMessageFor(e));
    }
  };

  const columns: Column<InstallerDownloadRow>[] = [
    {
      key: 'when',
      header: 'When',
      sortable: true,
      cell: (r) => (
        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
          {r.downloaded_at.slice(0, 16).replace('T', ' ')}Z
        </span>
      ),
    },
    {
      key: 'filename',
      header: 'Filename',
      cell: (r) => (
        <code
          style={{
            fontSize: '0.6875rem',
            background: 'var(--bg-overlay)',
            padding: '2px 6px',
            borderRadius: 4,
            border: '1px solid var(--border)',
          }}
        >
          {r.filename}
        </code>
      ),
    },
    {
      key: 'ip',
      header: 'IP',
      cell: (r) => (
        <code style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
          {r.ip_address ?? '—'}
        </code>
      ),
    },
    {
      key: 'agent',
      header: 'User agent',
      cell: (r) => (
        <span
          style={{
            fontSize: '0.75rem',
            color: 'var(--text-muted)',
            display: 'block',
            maxWidth: 280,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={r.user_agent ?? ''}
        >
          {r.user_agent ?? '—'}
        </span>
      ),
    },
    {
      key: 'email',
      header: 'Email hint',
      cell: (r) => r.optional_email_hint ? (
        <span style={{ fontSize: '0.75rem' }}>{r.optional_email_hint}</span>
      ) : (
        <span style={{ color: 'var(--text-muted)' }}>—</span>
      ),
    },
    ...(canMutate ? [{
      key: 'actions',
      header: '',
      align: 'right' as const,
      cell: (r: InstallerDownloadRow) => (
        <Button
          variant="ghost"
          size="sm"
          aria-label="Delete download record"
          onClick={(e) => { e.stopPropagation(); void onDelete(r.id); }}
        >
          <Trash2 width={12} height={12} />
        </Button>
      ),
    }] : []),
  ];

  return (
    <div className="page">
      <PageHeader
        title="Signup funnel"
        subtitle="Trial signup → installer download → engine activation → paid subscription. Stage-over-stage percentages show where customers drop off."
        icon={Filter}
        accent="#fb923c"
        actions={
          <Button variant="outline" size="sm" onClick={onRefresh}>
            <RefreshCw width={14} height={14}
              style={{ animation: refreshing ? 'spin 900ms linear infinite' : undefined }} />
            Refresh
          </Button>
        }
      />

      <CohortSelector cohorts={cohorts} value={cohort} onChange={setCohort} />

      <div style={{ marginTop: 16 }}>
        {data ? (
          <FunnelRibbon data={data} />
        ) : (
          <FetchState loading={!err} error={err} onRetry={onRefresh} skeleton="cards" />
        )}
      </div>

      <div style={{ marginTop: 16 }}>
        <DropOffCallout />
      </div>

      <h3 className="section-title" style={{ marginTop: 28 }}>Recent downloads</h3>
      <FetchState
        loading={!downloads && !err}
        error={err}
        empty={!!downloads && downloads.length === 0}
        emptyTitle="No downloads"
        emptyDescription="Installer downloads in the window will list here."
        onRetry={onRefresh}
      >
        <DataTable
          rows={downloads ?? []}
          columns={columns}
          rowKey={(r) => r.id}
          loading={false}
          emptyTitle="No downloads"
        />
      </FetchState>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Cohort selector
 * ────────────────────────────────────────────────────────────────── */

function CohortSelector({
  cohorts, value, onChange,
}: {
  cohorts: CohortRange[];
  value: CohortKey;
  onChange: (v: CohortKey) => void;
}): JSX.Element {
  return (
    <div
      style={{
        display: 'inline-flex', gap: 4, padding: 4,
        borderRadius: 12, border: '1px solid var(--border)', background: 'var(--bg-overlay)',
        flexWrap: 'wrap', width: 'fit-content',
      }}
    >
      {cohorts.map((c) => {
        const active = c.key === value;
        return (
          <button
            key={c.key}
            aria-pressed={active}
            title={c.hint}
            onClick={() => onChange(c.key)}
            style={{
              padding: '6px 12px',
              border: 'none',
              borderRadius: 8,
              background: active ? 'var(--bg-raised)' : 'transparent',
              color: active ? 'var(--text-primary)' : 'var(--text-muted)',
              fontSize: '0.75rem',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            {c.label}
          </button>
        );
      })}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Funnel ribbon
 * ────────────────────────────────────────────────────────────────── */

function FunnelRibbon({ data }: { data: FunnelSummary }): JSX.Element {
  const stages: Array<{
    label: string;
    value: number;
    note?: string;
    falloffPct?: number | null;
    tone: string;
  }> = [
    { label: 'Trials created',      value: data.trials_created,    tone: '#fb923c' },
    {
      label: 'Installer downloads',
      value: data.downloads,
      note: data.trials_created > 0
        ? `${pct(data.downloads, data.trials_created)}% of signups`
        : undefined,
      falloffPct: data.trials_created > 0
        ? Math.max(0, 100 - pct(data.downloads, data.trials_created))
        : null,
      tone: '#fbbf24',
    },
    {
      label: 'Activations',
      value: data.activations,
      note: data.download_to_activation_pct !== null
        ? `${data.download_to_activation_pct}% of downloads`
        : undefined,
      falloffPct: data.downloads > 0
        ? Math.max(0, 100 - pct(data.activations, data.downloads))
        : null,
      tone: '#22d3ee',
    },
    {
      label: 'Paid conversions',
      value: data.paid_conversions,
      note: data.trial_to_paid_pct !== null
        ? `${data.trial_to_paid_pct}% of trials`
        : undefined,
      falloffPct: data.activations > 0
        ? Math.max(0, 100 - pct(data.paid_conversions, data.activations))
        : null,
      tone: '#34d399',
    },
  ];
  const max = Math.max(1, ...stages.map((s) => s.value));

  return (
    <section
      style={{
        padding: 16,
        borderRadius: 14,
        border: '1px solid var(--border)',
        background: 'var(--bg-raised)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <ArrowDownRight width={14} height={14} color="var(--text-muted)" />
        <span
          style={{
            fontSize: '0.6875rem',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: 'var(--text-muted)',
            fontWeight: 600,
          }}
        >
          Funnel · stage-over-stage
        </span>
      </div>

      <ol
        style={{
          listStyle: 'none', padding: 0, margin: 0,
          display: 'flex', flexDirection: 'column', gap: 12,
        }}
      >
        {stages.map((s, i) => {
          const widthPct = (s.value / max) * 100;
          return (
            <li key={s.label}>
              {i > 0 && s.falloffPct !== null && s.falloffPct !== undefined && s.falloffPct > 0 && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    marginBottom: 6,
                    fontSize: '0.6875rem',
                    color: 'var(--warning)',
                  }}
                >
                  <ArrowDownRight width={11} height={11} />
                  <span>{s.falloffPct}% falloff from previous stage</span>
                </div>
              )}
              <div
                style={{
                  position: 'relative',
                  height: 44,
                  borderRadius: 10,
                  background: 'var(--bg-overlay)',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: `${Math.max(8, widthPct)}%`,
                    background: `linear-gradient(90deg, ${s.tone}55, ${s.tone}1f)`,
                    borderRight: `2px solid ${s.tone}`,
                    transition: 'width 320ms ease',
                  }}
                />
                <div
                  style={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    height: '100%',
                    padding: '0 14px',
                    gap: 10,
                  }}
                >
                  <span style={{
                    width: 22, height: 22, borderRadius: 999,
                    background: `${s.tone}33`, color: s.tone,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.6875rem', fontWeight: 600,
                  }}>{i + 1}</span>
                  <span style={{ fontSize: '0.8125rem', fontWeight: 500, flex: 1 }}>
                    {s.label}
                  </span>
                  <span
                    style={{
                      fontSize: '1rem', fontWeight: 600,
                      fontVariantNumeric: 'tabular-nums',
                      color: 'var(--text-primary)',
                    }}
                  >
                    {s.value.toLocaleString()}
                  </span>
                  {s.note && (
                    <StatusChip band="neutral" size="sm" noDot>{s.note}</StatusChip>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

function pct(numerator: number, denominator: number): number {
  if (denominator <= 0) return 0;
  return Math.round((numerator / denominator) * 100);
}

/* ──────────────────────────────────────────────────────────────────
 * Drop-off reasons callout
 * ────────────────────────────────────────────────────────────────── */

function DropOffCallout(): JSX.Element {
  return (
    <section
      style={{
        padding: 16,
        borderRadius: 14,
        border: '1px solid var(--border)',
        background: 'var(--bg-raised)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <AlertOctagon width={14} height={14} color="var(--warning)" />
        <h3
          style={{
            margin: 0,
            fontSize: '0.6875rem',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: 'var(--text-muted)',
            fontWeight: 600,
          }}
        >
          Common drop-off reasons
        </h3>
        <span
          style={{
            marginLeft: 'auto',
            fontSize: '0.6875rem',
            color: 'var(--text-muted)',
          }}
        >
          canonical exit paths · use to triage support patterns
        </span>
      </div>

      <div
        style={{
          display: 'grid',
          gap: 12,
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        }}
      >
        {DROP_OFF.map((stage) => (
          <article
            key={stage.stage}
            style={{
              padding: 12,
              borderRadius: 10,
              background: 'var(--bg-overlay)',
              border: '1px solid var(--border)',
            }}
          >
            <h4
              style={{
                margin: '0 0 8px',
                fontSize: '0.75rem',
                fontWeight: 600,
                color: 'var(--text-primary)',
              }}
            >
              {stage.stage}
            </h4>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {stage.reasons.map((r) => (
                <li
                  key={r.label}
                  style={{
                    fontSize: '0.75rem',
                    color: 'var(--text-muted)',
                    paddingLeft: 8,
                    borderLeft: '2px solid var(--accent)',
                  }}
                >
                  <div style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
                    {r.label}
                  </div>
                  <div>{r.hint}</div>
                </li>
              ))}
            </ul>
          </article>
        ))}
      </div>
    </section>
  );
}
